import { COMMA, SEMICOLON, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FilterType, GlobalSearchResults, ResultsDatum, filterTypes } from '../../models/global-search';
import { RecentSearchService } from 'src/app/core/services/recent-search.service';
import { GlobalSearchService } from 'src/app/core/services/global-search.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  standalone: false,
})
export class GlobalSearchComponent implements OnInit {
  filterTypes = filterTypes;
  filterType: FilterType = 'all';

  searchTerms: string[] = [];
  searchInput: string = '';

  maxNumberOfSearchTerms = 5;
  minTermLength = 3;

  showMinTermLengthTooltip = false;
  showDuplicateTermsTooltip = false;
  showRecentSearches = false;
  showSearchResults = false;
  recentSearches = [];
  searchResults: GlobalSearchResults[] = [];
  sortedSearchResults: GlobalSearchResults[] = [];

  isFetchingResults = false;

  readonly separatorKeysCodes = [ENTER, COMMA, SEMICOLON] as const;

  @ViewChild('searchInputVC') searchInputVC: ElementRef<HTMLInputElement>;

  /* The `@HostListener` decorator is used to listen for events on the host element, which is the
  element that the component is attached to. In this case, the `document:click` event is being
  listened for in order hide the search results or recent search */
  @HostListener('document:click', ['$event'])
  onClick(event: any): void {
    const searchResultDiv = this.elementRef.nativeElement.querySelector('.global-search-results');
    const recentSearchDiv = this.elementRef.nativeElement.querySelector('.recent-search');
    if (searchResultDiv && !searchResultDiv.contains(event.target)) {
      this.showSearchResults = false;
    }

    if (recentSearchDiv && !recentSearchDiv.contains(event.target)) {
      this.showRecentSearches = false;
    }
  }

  /* The `@HostListener('paste', [''])` decorator is used to listen for the 'paste' event on the
  host element, which is the element that the component is attached to. It is used to parse the elements pasted into search Terms */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text');
    const CHARACTER_SEPARATORS = ['\t', '\r\n', ',', ';'];
    const regex = new RegExp(CHARACTER_SEPARATORS.join('|'), 'g');

    const values = pastedText.split(regex);

    //remove duplicated values
    const uniqueValues = [...new Set(values)];

    //notify that some values were removed because they were duplicated
    if (uniqueValues.length < values.length) {
      //open snackbar
      this.snackBar.openFromComponent(SnackbarComponent, {
        data: {
          message: `Some values were removed because they were duplicated`,
          closeButton: true,
        },
      });
    }

    uniqueValues.forEach((value) => {
      if (this.searchTerms.length < this.maxNumberOfSearchTerms) {
        this.addSearchTerm(value);
      }
    });

    setTimeout(() => {
      this.clearInputValue();
    }, 50);
  }

  constructor(
    private recentSearchService: RecentSearchService,
    private globalSearchService: GlobalSearchService,
    private elementRef: ElementRef,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.filterType = 'all';
    this.recentSearches = this.recentSearchService.listUserSearches();
  }

  /**
   * The function updates the search input value and hides certain tooltips and recent searches.
   * @param {Event} event - The event parameter is an object that represents the event that triggered
   * the input change. It contains information about the event, such as the target element that
   * triggered the event.
   */
  onInputChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;

    this.searchInput = (value || '').trim();
    this.showMinTermLengthTooltip = false;
    this.showDuplicateTermsTooltip = false;
    this.showRecentSearches = false;
  }

  /**
   * Evalutes if showing the recent search on input focus
   */
  onInputFocus() {
    if (this.searchTerms.length === 0 && this.searchInput.length === 0) {
      setTimeout(() => {
        if (this.recentSearches?.length > 0) {
          this.showSearchResults = false;
          this.showRecentSearches = true;
        }
      }, 200);
    }
  }

  /**
   * The function sets the search terms to the recent search array, hides the recent searches, and
   * submits the search.
   * @param {string[]} recentSearch - The parameter "recentSearch" is an array of strings that
   * represents the recent search terms that the user has clicked on.
   */
  onRecentSearchClicked(recentSearch: string[]) {
    this.searchTerms = [...recentSearch];
    this.showRecentSearches = false;
    this.submitSearch();
  }

  /**
   * Clears the user's recent searches and updates the list of
   * recent searches.
   */
  clearRecentSearches() {
    this.recentSearchService.clearUserSearches();
    this.recentSearches = this.recentSearchService.listUserSearches();
  }

  /**
   * The function handles key press events for ',', ';' and 'enter' keys,
   * checks for various conditions, and adds search terms to an array.
   * @param {Event} event - The event parameter is an object that represents the event that occurred.
   * In this case, it is an event that is triggered when a key is pressed on the keyboard.
   * @returns nothing (undefined) in most cases. However, it may return early and not execute the rest
   * of the code if certain conditions are met.
   */
  onKeyPressed(event: Event) {
    const keyboardEvent = event as KeyboardEvent;

    if (this.searchTerms.length > this.maxNumberOfSearchTerms) {
      return;
    }

    if (this.searchInput.length !== 0 && this.searchInput.length < this.minTermLength) {
      this.showMinTermLengthTooltip = true;
      return;
    }

    //check if term is duplicated
    if (this.searchTerms.includes(this.searchInput.toLowerCase())) {
      this.showDuplicateTermsTooltip = true;
      return;
    }

    //check if the key pressed is enter
    if (keyboardEvent.key === 'Enter') {
      if (this.searchTerms.length > 0 && this.searchInput.length === 0) {
        this.submitSearch();
      }
    }

    const value = (event.target as HTMLInputElement).value;
    this.addSearchTerm(value);
  }

  /**
   * The function checks if the search input meets the minimum term length requirement, adds the search
   * term to a list, and submits the search.
   */
  onSearchIconClicked() {
    if (this.searchInput.length >= this.minTermLength) {
      this.addSearchTerm(this.searchInput);
    }
    this.submitSearch();
  }

  /**
   * The submitSearch function performs a search in the backend using the provided search terms,
   * updates the search results, and displays them on the page.
   * @returns nothing.
   */
  submitSearch(): void {
    //searching in the backend
    if (this.searchTerms.length == 0) {
      return;
    }

    this.showSearchResults = false;
    this.isFetchingResults = true;
    this.recentSearchService.addUserSearch(this.searchTerms);
    this.globalSearchService.getSearchResults(this.searchTerms, this.filterType).subscribe((response) => {
      this.searchResults = response as GlobalSearchResults[];
      this.sortedSearchResults = this.sortSearchResults(this.searchResults);
      this.isFetchingResults = false;
      this.showRecentSearches = false;
      setTimeout(() => {
        this.showSearchResults = true;
      }, 100);
    });
    this.recentSearches = this.recentSearchService.listUserSearches();
  }

  /* The `sortSearchResults` function is used to sort the search results based on certain criteria. */
  sortSearchResults(searchResults: GlobalSearchResults[]): GlobalSearchResults[] {
    // Modules with the least number of matches will be shown first.
    // Except those with 0 matches, those will be shown last
    // Except those the user doesn’t have access (they’ll be shown last)

    const sortedSearchResults = searchResults.sort((a, b) => {
      if (a.results.data.length > 0 && a.results.total > 0 && b.results.data.length > 0 && b.results.total > 0) {
        return a.results.data.length - b.results.data.length;
      } else if (
        a.results.data.length === 0 &&
        a.results.total === 0 &&
        b.results.data.length === 0 &&
        b.results.total > 0
      ) {
        return -1;
      } else if (a.results.data.length === 0) {
        return 1;
      } else if (b.results.data.length === 0) {
        return -1;
      } else {
        return 0;
      }
    });

    return sortedSearchResults;
  }

  /**
   * The function "onResultClicked" sets the "showSearchResults" variable to false and clears the
   * "searchTerms" array.
   */
  onResultClicked(): void {
    this.showSearchResults = false;
    this.searchTerms = [];
  }

  /**
   * It renders the values of the filter menu.
   */
  renderFilterType(filterType: FilterType): string {
    switch (filterType) {
      case 'all':
        return 'All';
      case 'ticket-number':
        return 'Ticket Number';
      case 'customer-ticket-number':
        return 'Cust. Ticket Number';
      case 'asset-tag':
        return 'Asset Tag';
      case 'serial-numbers':
        return 'Serial Number';
      case 'cpo':
        return 'Purchase Order';
      case 'tracking-numbers':
        return 'Tracking Number';
    }
  }

  /**
   * The function `disableSearchButton` returns a boolean value indicating whether the search button
   * should be disabled based on the length of the search terms and whether results are currently being
   * fetched.
   * @returns a boolean value.
   */
  disableSearchButton(): boolean {
    return (!this.searchTerms.length && this.searchInput.length < this.minTermLength) || this.isFetchingResults;
  }

  /**
   * The function adds a search term to an array if it is not empty, clears the input value, and
   * focuses on the search input field.
   * @param {string} eventValue - The eventValue parameter is a string that represents the value of an
   * input event.
   * @returns void, which means it does not return any value.
   */
  addSearchTerm(eventValue: string): void {
    const value = (eventValue || '').trim().toLowerCase();

    if (value == '') {
      return;
    }

    if (value) {
      this.searchTerms.push(value);
    }

    this.clearInputValue();

    this.searchInputVC.nativeElement.blur();
    this.searchInputVC.nativeElement.focus();
  }

  /**
   * The function removes a search term from an array and sets focus on a search input field.
   * @param {string} value - The value parameter is a string that represents the search term to be
   * removed from the searchTerms array.
   */
  removeSearchTerm(value: string): void {
    const index = this.searchTerms.indexOf(value);

    if (index >= 0) {
      this.searchTerms.splice(index, 1);

      setTimeout(() => {
        this.searchInputVC.nativeElement.focus();
      }, 50);
    }
  }

  /**
   * The function clears the value of a search input field.
   */
  clearInputValue() {
    this.searchInputVC.nativeElement.value = '';
    this.searchInput = '';
  }
}
